import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Lock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// v52: FastAPI `detail` can be a string, an object (our MFA payload) or a
// 422 array. Passing a non-string to toast/JSX crashes the login screen.
const formatApiError = (detail, fallback = 'An error occurred') => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ') || fallback;
  }
  if (typeof detail === 'object') {
    if (detail.mfa_required) return detail.message || 'This account requires a verification code.';
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.msg === 'string') return detail.msg;
  }
  return fallback;
};

const Login = ({ onLogin }) => {
  const isLogin = true; // patch_v49b: self-service signup removed, login only
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    department: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        toast.success('Login successful!');
        onLogin(response.data.access_token, response.data.user);
      }
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail, 'Unable to sign in. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1692889783742-7d99b124c402?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-blue-900/60 backdrop-blur-[2px]" />
        <div className="relative z-10 w-full h-full flex items-center justify-center">
          <div className="text-center px-12">
            <div className="w-20 h-20 bg-white rounded-2xl mx-auto mb-8 flex items-center justify-center shadow-apple-lg">
              <FileText size={40} weight="bold" className="text-blue-900" />
            </div>
            <h1 className="text-5xl font-semibold text-white mb-4 tracking-tight">
              Document Status
            </h1>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-blue-900 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <FileText size={32} weight="bold" className="text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-blue-900">Document Status</h1>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-6">
              <Lock size={24} className="text-gray-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Welcome back
            </h2>
            <p className="text-gray-500">
              Enter your credentials to access your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="full_name" className="text-sm text-gray-700">Full Name</Label>
                <Input
                  id="full_name"
                  data-testid="full-name-input"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                  placeholder="John Smith"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm text-gray-700">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm text-gray-700">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                placeholder="••••••••"
              />
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="department" className="text-sm text-gray-700">Department (Optional)</Label>
                <Input
                  id="department"
                  data-testid="department-input"
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="h-12 rounded-xl bg-gray-50 border-gray-200 focus:bg-white transition-colors"
                  placeholder="Engineering"
                />
              </div>
            )}

            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-medium text-base mt-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>

          <p className="text-center text-gray-400 text-xs mt-8">
            © {new Date().getFullYear()} Document Status. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
